import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppError } from '../core/error/appError';
import type { Availability } from '../core/model/availability';
import type { Reservation } from '../core/model/reservation';
import type { Restaurant, TimeSlot } from '../core/model/restaurant';
import { PhoneNormalizer } from '../core/util/phoneNormalizer';
import type { LocalDate, LocalTime } from '../core/util/datetime';
import { nowDateTime } from '../core/util/datetime';
import { BookingValidator, MAX_NOTES_LENGTH } from '../core/validation/bookingValidator';
import { BookingField, type FieldErrors } from '../core/validation/fieldError';
import { currentlyConnected } from '../data/repository/apiCall';
import { availabilityStateOf, type AvailabilityState } from './availabilityState';
import { useContainer } from './ContainerContext';
import { useCriteria } from './CriteriaContext';
import { useSession } from './SessionContext';
import { readJson, remove, storageKey, writeJson } from '../data/local/storage';

/**
 * Borrador de reserva compartido por los tres pasos del flujo.
 *
 * Lo que el usuario ya escribió se guarda en `localStorage` para que sobreviva a una recarga de
 * la pestaña: perder el formulario a mitad del checkout es una de las causas más comunes de
 * abandono.
 */
export interface BookingDraft {
  readonly restaurantId: string;
  readonly date: LocalDate;
  readonly time: LocalTime;
  readonly partySize: number;
  readonly selectedSlot: TimeSlot | null;
  readonly name: string;
  readonly phone: string;
  readonly email: string;
  readonly occasion: string | null;
  readonly notes: string;
  readonly termsAccepted: boolean;
  /** Estable durante toda la vida del borrador: hace idempotente el POST. */
  readonly idempotencyKey: string;
  /** No nulo cuando el flujo modifica una reserva existente en lugar de crear una. */
  readonly editingReservationId: string | null;
}

export type BookingEvent =
  | { readonly kind: 'CONFIRMED'; readonly reservation: Reservation; readonly wasModification: boolean }
  /** El slot se ocupó: hay que volver al primer paso, no reintentar. */
  | { readonly kind: 'SLOT_TAKEN' }
  | { readonly kind: 'REQUIRES_LOGIN' }
  | { readonly kind: 'FAILED'; readonly error: AppError };

const DRAFT_KEY = storageKey('booking-draft');

function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useBooking(restaurantId: string, editingReservationId: string | null) {
  const { restaurants, reservations } = useContainer();
  const { criteria } = useCriteria();
  const { session } = useSession();

  const [draft, setDraft] = useState<BookingDraft>(() => {
    const stored = readJson<BookingDraft>(DRAFT_KEY);
    // Un borrador de otro restaurante no se restaura: mezclar dos reservas a medias es peor
    // que empezar de cero.
    if (stored && stored.restaurantId === restaurantId) return stored;
    return {
      restaurantId,
      date: criteria.date,
      time: criteria.time,
      partySize: criteria.partySize,
      selectedSlot: null,
      name: '',
      phone: '',
      email: '',
      occasion: null,
      notes: '',
      termsAccepted: false,
      idempotencyKey: newIdempotencyKey(),
      editingReservationId,
    };
  });

  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [availability, setAvailability] = useState<AvailabilityState>({ status: 'LOADING' });
  const [event, setEvent] = useState<BookingEvent | null>(null);
  const availabilityRequest = useRef(0);

  useEffect(() => {
    writeJson(DRAFT_KEY, draft);
  }, [draft]);

  // Prellenado desde la sesión: quien ya inició sesión no vuelve a teclear su nombre.
  useEffect(() => {
    if (session.kind !== 'AUTHENTICATED') return;
    setDraft((current) => ({
      ...current,
      name: current.name || session.user.fullName,
      email: current.email || session.user.email,
      phone: current.phone || session.user.phone || '',
    }));
  }, [session]);

  useEffect(() => {
    let active = true;
    restaurants.getRestaurant(restaurantId).then((result) => {
      if (active && result.ok) setRestaurant(result.data);
    });
    return () => {
      active = false;
    };
  }, [restaurants, restaurantId]);

  const loadAvailability = useCallback(
    (date: LocalDate, time: LocalTime, partySize: number) => {
      const id = ++availabilityRequest.current;
      setAvailability({ status: 'LOADING' });
      restaurants.getAvailability(restaurantId, date, time, partySize).then((result) => {
        // Descarta la respuesta de una petición que ya no es la última: cambiar de fecha dos
        // veces seguidas no puede dejar en pantalla los horarios de la primera.
        if (id !== availabilityRequest.current) return;
        setAvailability(
          result.ok
            ? availabilityStateOf(result.data as Availability)
            : { status: 'ERROR', error: result.error },
        );
      });
    },
    [restaurants, restaurantId],
  );

  useEffect(() => {
    loadAvailability(draft.date, draft.time, draft.partySize);
  }, [loadAvailability, draft.date, draft.time, draft.partySize]);

  const clearError = (field: string) =>
    setErrors((current) => {
      const { [field]: _removed, ...rest } = current;
      return rest;
    });

  const actions = useMemo(
    () => ({
      selectSlot(slot: TimeSlot) {
        setDraft((d) => ({ ...d, selectedSlot: slot, time: slot.time }));
        clearError(BookingField.TIME);
      },
      // Al cambiar la fecha, el slot elegido deja de ser válido: mantenerlo seleccionado
      // llevaría a confirmar una mesa de otro día.
      setDate(date: LocalDate) {
        setDraft((d) => ({ ...d, date, selectedSlot: null }));
      },
      setTime(time: LocalTime) {
        setDraft((d) => ({ ...d, time, selectedSlot: null }));
      },
      setPartySize(partySize: number) {
        setDraft((d) => ({ ...d, partySize, selectedSlot: null }));
      },
      setName(name: string) {
        setDraft((d) => ({ ...d, name }));
        clearError(BookingField.NAME);
      },
      setPhone(phone: string) {
        setDraft((d) => ({ ...d, phone }));
        clearError(BookingField.PHONE);
      },
      setEmail(email: string) {
        setDraft((d) => ({ ...d, email }));
        clearError(BookingField.EMAIL);
      },
      setOccasion(occasion: string | null) {
        setDraft((d) => ({ ...d, occasion }));
      },
      setNotes(notes: string) {
        // Se corta en el límite en lugar de dejar escribir y rechazar al final.
        setDraft((d) => ({ ...d, notes: notes.slice(0, MAX_NOTES_LENGTH) }));
        clearError(BookingField.NOTES);
      },
      setTermsAccepted(termsAccepted: boolean) {
        setDraft((d) => ({ ...d, termsAccepted }));
        clearError(BookingField.TERMS);
      },
      clearEvent() {
        setEvent(null);
      },
      /** Se llama al confirmar: el borrador ya no debe reaparecer en la próxima reserva. */
      discardDraft() {
        remove(DRAFT_KEY);
      },
    }),
    [],
  );

  /** Validación al perder el foco: un error por cada tecla sería hostil. */
  const validateField = useCallback(
    (field: string) => {
      const error =
        field === BookingField.NAME
          ? BookingValidator.validateName(draft.name)
          : field === BookingField.PHONE
            ? BookingValidator.validatePhone(draft.phone)
            : field === BookingField.EMAIL
              ? BookingValidator.validateEmail(draft.email)
              : null;
      setErrors((current) => {
        if (!error) {
          const { [field]: _removed, ...rest } = current;
          return rest;
        }
        return { ...current, [field]: error };
      });
    },
    [draft.name, draft.phone, draft.email],
  );

  const validateSlotStep = useCallback(
    (now = nowDateTime()): boolean => {
      const base = BookingValidator.validateSlotStep(
        draft.date,
        draft.selectedSlot?.time ?? draft.time,
        draft.partySize,
        now,
        restaurant,
      );
      // Sin slot elegido no hay nada que reservar; se marca sobre el campo de hora.
      const next =
        draft.selectedSlot === null && !base[BookingField.TIME]
          ? { ...base, [BookingField.TIME]: 'TIME_PAST' as const }
          : base;
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [draft, restaurant],
  );

  const validateGuestStep = useCallback((): boolean => {
    const next = BookingValidator.validateGuestStep(draft.name, draft.phone, draft.email, draft.notes);
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [draft]);

  const submit = useCallback(
    async (now = nowDateTime()) => {
      const validation = {
        ...BookingValidator.validateReviewStep(draft.termsAccepted),
        ...BookingValidator.validateSlotStep(draft.date, draft.time, draft.partySize, now, restaurant),
        ...BookingValidator.validateGuestStep(draft.name, draft.phone, draft.email, draft.notes),
      };
      if (Object.keys(validation).length > 0) {
        setErrors(validation);
        return;
      }

      // Reservar es la única operación que exige sesión. Se comprueba aquí, al final, y no al
      // abrir la app: todo el descubrimiento funciona como invitado.
      if (session.kind !== 'AUTHENTICATED') {
        setEvent({ kind: 'REQUIRES_LOGIN' });
        return;
      }

      if (!currentlyConnected()) {
        setEvent({ kind: 'FAILED', error: { kind: 'OFFLINE' } });
        return;
      }

      const slot = draft.selectedSlot;
      if (!slot) {
        setErrors({ [BookingField.TIME]: 'TIME_PAST' });
        return;
      }

      setSubmitting(true);
      const result = draft.editingReservationId
        ? await reservations.update(draft.editingReservationId, {
            date: draft.date,
            time: slot.time,
            partySize: draft.partySize,
            slotToken: slot.token,
            occasion: draft.occasion,
            notes: draft.notes.trim() || null,
          })
        : await reservations.create({
            restaurantId: draft.restaurantId,
            date: draft.date,
            time: slot.time,
            partySize: draft.partySize,
            slotToken: slot.token,
            guestName: draft.name.trim(),
            guestPhone: PhoneNormalizer.normalize(draft.phone) ?? draft.phone,
            guestEmail: draft.email.trim(),
            occasion: draft.occasion,
            notes: draft.notes.trim() || null,
            idempotencyKey: draft.idempotencyKey,
          });
      setSubmitting(false);

      if (result.ok) {
        setEvent({
          kind: 'CONFIRMED',
          reservation: result.data,
          wasModification: draft.editingReservationId !== null,
        });
        return;
      }

      switch (result.error.kind) {
        case 'SLOT_TAKEN':
          // La mesa ya no existe: se limpia la selección y se recarga la disponibilidad para
          // que el usuario elija sobre datos frescos.
          setDraft((d) => ({ ...d, selectedSlot: null }));
          loadAvailability(draft.date, draft.time, draft.partySize);
          setEvent({ kind: 'SLOT_TAKEN' });
          break;
        case 'SESSION_EXPIRED':
          setEvent({ kind: 'REQUIRES_LOGIN' });
          break;
        case 'VALIDATION':
          setErrors(result.error.fields);
          break;
        default:
          setEvent({ kind: 'FAILED', error: result.error });
      }
    },
    [draft, restaurant, session, reservations, loadAvailability],
  );

  return {
    draft,
    errors,
    isSubmitting,
    restaurant,
    availability,
    event,
    ...actions,
    validateField,
    validateSlotStep,
    validateGuestStep,
    submit,
    reloadAvailability: () => loadAvailability(draft.date, draft.time, draft.partySize),
  };
}

export type BookingController = ReturnType<typeof useBooking>;
